/**
 * RunResultRows — the per-test outcome list for a run (#2304).
 *
 * One single-line row per test: status icon, name, duration. Extracted so the
 * live-run card and the past-run detail pane render the SAME rows from one
 * place — the card gained these rows in #2304, and a second copy of the markup
 * would drift from the pane's. Presentational only; the caller owns where it
 * sits and what heading (if any) precedes it. Renders nothing when the run has
 * no results yet, so it is safe to drop into a card while a run is still going.
 */
import { CheckCircle2, XCircle, Clock } from 'lucide-react'
import { formatDuration } from '@/lib/utils'
import type { TestRun } from '@/types'

/** A run/test status → its icon. Exported so the Runs page's run-list and the
 * rows here read a status the same way, from one definition. Unknown/other
 * statuses fall through to a muted clock. */
export function statusIcon(status: string) {
  switch (status) {
    case 'passed': return <CheckCircle2 className="h-4 w-4 text-success" />
    case 'failed': return <XCircle className="h-4 w-4 text-destructive" />
    case 'running': return <Clock className="h-4 w-4 text-primary animate-pulse" />
    default: return <Clock className="h-4 w-4 text-muted-foreground" />
  }
}

export function RunResultRows({ results }: { results: TestRun['results'] }) {
  if (!results || results.length === 0) return null
  return (
    <div className="space-y-1">
      {results.map((result) => (
        <div
          key={result.id}
          className="border rounded-lg flex items-center gap-2 p-2.5"
        >
          {statusIcon(result.status)}
          <span className="flex-1 text-left text-sm">{result.name}</span>
          <span className="text-xs text-muted-foreground">{formatDuration(result.duration)}</span>
        </div>
      ))}
    </div>
  )
}
